import React, { useEffect, useRef, useState } from "react";
import { createStructuredSelector } from "reselect";
import { connect } from "react-redux";
import isEqual from "lodash/isEqual";
import mixpanel from "mixpanel-browser";
import ReactTooltip from "react-tooltip";

import { actions as modelActions } from "features/Model/_ducks";
import { fetchDrawingData } from "../DrawingNew/_ducks/thunk";
import { setPrimaryUnits } from "../_ducks/thunk";
import { setSaveTimeout } from "../_ducks/actions";
import { toggleImageGallery } from "store/imageGallery/actions";

import { useStateSelector } from "utils/field";
import { UNIT_SYSTEMS } from "components/Input/InputWithUnits";
import { selectFormErrors } from "store/form/selectors";

import Button from "components/Button";
import ToggleMulti from "components/ToggleMulti";
import SnackbarAlert from "components/Alert";
import DrawingIcon from "assets/images/icons/JSX/Drawing";
import Save from "assets/images/icons/JSX/save";
import Checklist from "assets/images/icons/Icon-Checklist.svg?react";
import SettingsIcon from "assets/images/icons/JSX/Settings";
import PhotoGalleryIcon from "assets/images/icons/ImageGallery.svg?react";
import ResetIcon from "assets/images/icons/Icon-Reset-blue.svg?react";
import VersionHistory from "assets/images/icons/JSX/VersionHistory";

import classes from "./style.module.scss";

const { toggleContentOpen, changeDataentryMode } = modelActions;

const TopButtons = ({
    submitting = false,
    simulationStatus,
    toggleNewDrawingOpen,
    handleSubmit,
    invalid = false,
    toggleChecklist,
    saveTimeout,
    toggleSettings,
    toggleVersionHistory,
    isNewSnapshot,
    fetchModelData,
    isSyncing,
    formErrors,
    fetchDrawingData,
    toggleContentOpen,
    changeDataentryMode,
    setSaveTimeout,
    toggleImageGallery,
    setPrimaryUnits,
}) => {
    const { uid } = useStateSelector("user");
    const {
        modelId,
        modelData: formModelData = {},
        modelDetails: formModelDetails = {},
        checklist: { list = {} } = {},
        contentOpen = false,
        dataEntryMode = "",
        dataEntryModeToCompare = "",
        modelDataToCompare = {},
        modelDetailsToCompare = {},
        modelTakeoffToCompare = {},
        modelTakeoff,
        linkedBaselineToCompare = {},
        linkedBaseline,
        greenButtonDataLoading = false,
        greenButtonUploadLoading = false,
        greenButtonWeatherAnalysisLoading = false,
        greenButtonBaseLoadCalculationLoading = false,
    } = useStateSelector("model");
    const { packages: upgradePackages = {}, initPackages: initUpgradePackages = {} } = useStateSelector("upgrades");
    const { items: chbaChecklistItems = {}, itemsToCompare: chbaChecklistItemsToCompare = {} } =
        useStateSelector("chbaChecklist");
    const { isImageGalleryOpen = false } = useStateSelector("imageGallery");

    const {
        owner: { uid: ownerId = uid } = {},
        writePermission = [],
        isSimulationRunning: simulationRunning = false,
    } = formModelDetails;

    const hasChecklist = Object.keys(list).length > 0 || false;
    const hasUnchecked = Object.keys(list).some((id) => !list[id]?.addressed && !list[id]?.dismissed) || false;

    const readOnly = ownerId !== uid && !writePermission.includes(uid);
    const utilityGreenButtonProcessing =
        greenButtonDataLoading ||
        greenButtonUploadLoading ||
        greenButtonWeatherAnalysisLoading ||
        greenButtonBaseLoadCalculationLoading;
    const isDataChanged =
        !isEqual(formModelData, modelDataToCompare) ||
        !isEqual({ ...modelDetailsToCompare, lastEdited: {} }, { ...formModelDetails, lastEdited: {} }) ||
        !isEqual(upgradePackages, initUpgradePackages) ||
        !isEqual(modelTakeoffToCompare, modelTakeoff) ||
        !isEqual(linkedBaselineToCompare, linkedBaseline) ||
        !isEqual(chbaChecklistItems, chbaChecklistItemsToCompare) ||
        (dataEntryMode ?? "").toLowerCase() !== (dataEntryModeToCompare ?? "").toLowerCase() ||
        isImageGalleryOpen;

    // const [mode, changeMode] = useState("Classic");

    // useEffect(() => {
    //     if (isDataChanged) {
    //         clearTimeout(saveTimeout);

    //         setSaveTimeout(setTimeout(() => handleSubmit(), 60000));
    //     } else {
    //         clearTimeout(saveTimeout);
    //     }

    //     return () => clearTimeout(saveTimeout);
    // }, [isDataChanged]);

    useEffect(() => {
        if (isDataChanged) {
            const unloadCallback = (event) => {
                event.preventDefault();
                event.returnValue = "";
                return "";
            };

            window.addEventListener("beforeunload", unloadCallback);
            return () => window.removeEventListener("beforeunload", unloadCallback);
        }
    }, [isDataChanged]);

    const isSimulationRunning = simulationStatus === "RUNNING" || simulationRunning;

    const { uiSettings: { primaryUnits = "Metric" } = {} } = formModelData;

    const nextUnitSystem = UNIT_SYSTEMS[(UNIT_SYSTEMS.indexOf(primaryUnits) + 1) % UNIT_SYSTEMS.length];

    const unitsTipRef = useRef(null);
    const switchedUnitsFromButton = useRef(false);
    const [switchingUnits, setSwitchingUnits] = useState(false);
    const unitsSwitchFrame = useRef(null);

    // react-tooltip caches the tip content when it opens, so re-open it against the
    // updated label once the switch has rendered
    useEffect(() => {
        if (!switchedUnitsFromButton.current) return;

        switchedUnitsFromButton.current = false;

        if (unitsTipRef.current) {
            ReactTooltip.show(unitsTipRef.current);
        }
    }, [primaryUnits]);

    useEffect(
        () => () => {
            if (unitsSwitchFrame.current) cancelAnimationFrame(unitsSwitchFrame.current);
        },
        []
    );

    const handleSwitchUnits = () => {
        if (switchingUnits) return;

        mixpanel.track("Primary Units Change", {
            Units: nextUnitSystem,
        });
        switchedUnitsFromButton.current = true;
        setSwitchingUnits(true);

        // Converting every field is synchronous and blocks the main thread long enough to be
        // noticeable on component-heavy tabs, so hand control back to the browser for a full
        // frame first. Without this the spinner is only ever committed, never painted.
        unitsSwitchFrame.current = requestAnimationFrame(() => {
            unitsSwitchFrame.current = requestAnimationFrame(() => {
                unitsSwitchFrame.current = null;

                try {
                    setPrimaryUnits(nextUnitSystem);
                } finally {
                    setSwitchingUnits(false);
                }
            });
        });
    };

    return (
        <div className={classes.buttons}>
            {hasChecklist && (
                <div data-for="drawingTip" data-tip="Notes generated from homeowner inputs">
                    <Button
                        smallPadding
                        type="emptyBg"
                        icon={Checklist}
                        onClick={async () => {
                            mixpanel.track("Checklist Open");
                            toggleChecklist(true);
                        }}
                        className={classes.checklistButton}
                    >
                        {hasUnchecked && <span className={classes.alertDot}></span>} Checklist
                    </Button>
                </div>
            )}
            <div
                ref={unitsTipRef}
                data-for="unitsTip"
                data-html
                data-tip={
                    readOnly
                        ? "Display units cannot be changed in read-only mode"
                        : isSimulationRunning
                          ? "Display units cannot be changed while a simulation is running"
                          : `<span style="display:block;text-align:center;">Switch display units to <strong>${nextUnitSystem}</strong>.<em style="display:block;">(All fields will be reset to that system's default units)</em></span>`
                }
            >
                <Button
                    smallPadding
                    type="emptyBg"
                    onClick={handleSwitchUnits}
                    loading={switchingUnits}
                    disabled={readOnly || isSimulationRunning}
                    className={classes.unitSystem}
                >
                    {primaryUnits}
                </Button>
            </div>
            <div
                data-for="drawingTip"
                data-tip={
                    formErrors.length > 0
                        ? formErrors[0]
                            ? `Resolve issues with: ${formErrors[0].label}`
                            : "Please resolve invalid fields"
                        : "Build your model using Volta SNAP's drawing capture."
                }
            >
                <Button
                    smallPadding
                    type="emptyBg"
                    icon={DrawingIcon}
                    onClick={async () => {
                        clearTimeout(saveTimeout);

                        if (dataEntryMode.toLowerCase() === "hot2000") await changeDataentryMode("combined");

                        await fetchDrawingData(modelId);
                        await handleSubmit();
                        mixpanel.track("Drawing Open");
                        toggleNewDrawingOpen(true);
                    }}
                    disabled={formErrors.length > 0}
                    className={classes.drawing}
                />
            </div>
            {/* TODO: update copy for tool tip */}
            <div
                data-for="photoGalleryTip"
                data-tip={
                    formErrors.length > 0
                        ? formErrors[0]
                            ? `Resolve issues with: ${formErrors[0].label}`
                            : "Please resolve invalid fields"
                        : "Photo Gallery"
                }
            >
                <Button
                    smallPadding
                    type="emptyBg"
                    icon={PhotoGalleryIcon}
                    onClick={async () => {
                        clearTimeout(saveTimeout);

                        toggleImageGallery();

                        await handleSubmit();
                        mixpanel.track("Photo Gallery Open");
                    }}
                    disabled={formErrors.length > 0}
                    className={classes.drawing}
                />
            </div>
            <div data-for="drawingTip" data-tip={"Version History"}>
                <Button
                    smallPadding
                    type="emptyBg"
                    icon={VersionHistory}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={async () => {
                        mixpanel.track("Version History Open");
                        toggleVersionHistory(true);
                    }}
                    className={classes.versionHistoryButton}
                />
            </div>
            <div data-for="drawingTip" data-tip="Change your model's settings">
                <Button
                    smallPadding
                    type="emptyBg"
                    icon={SettingsIcon}
                    onClick={async () => {
                        mixpanel.track("Model Settings Open");
                        toggleSettings(true);
                    }}
                    className={classes.settingsButton}
                />
            </div>
            <div
                className={classes.toggleContent}
                data-for="openToggleTip"
                data-tip={`<span style="text-align:center"><span style="display:block;">Toggle all expanding content for pdf/print.</span><em style="display:block;">(Opening all content will likely slow down the application)</em></span>`}
                data-html
            >
                <ToggleMulti
                    className={classes.toggle}
                    active={contentOpen ? "yes" : "no"}
                    options={[
                        {
                            id: "no",
                            label: "Close",
                            onClick: toggleContentOpen,
                        },
                        {
                            id: "yes",
                            label: "Open",
                            onClick: toggleContentOpen,
                        },
                    ]}
                />
            </div>
            <div
                className={classes.saveWrapper}
                data-for="saveTip"
                data-tip={(() => {
                    if (formErrors.length > 0) {
                        return formErrors[0]
                            ? `Resolve issues with: ${formErrors[0].label}`
                            : "Please resolve invalid fields";
                    }
                    if (readOnly) {
                        return "Saving disabled in read-only mode";
                    }
                    if (invalid) {
                        return "Please resolve invalid fields";
                    } else if (submitting) {
                        return "Saving...";
                    } else if (utilityGreenButtonProcessing) {
                        return "Saving disabled while Green Button utility data is processing";
                    } else if (!isDataChanged || isSimulationRunning) {
                        return "There are no changes to save";
                    }

                    return "Save model";
                })()}
            >
                <Button
                    style={{ minWidth: "9.625rem" }}
                    smallPadding
                    icon={() => <Save strokeClass={classes.saveIconStroke} />}
                    onClick={async () => {
                        clearTimeout(saveTimeout);
                        // await saveDrawingData();

                        //saveDrawingData handled in an action and cannot yet be disabled at the source, like handleSubmit
                        if (!readOnly) {
                            // await saveDrawingData();
                        }

                        mixpanel.track("Save Model");
                        handleSubmit();
                    }}
                    disabled={
                        isSimulationRunning ||
                        submitting ||
                        invalid ||
                        !isDataChanged ||
                        readOnly ||
                        formErrors.length > 0 ||
                        utilityGreenButtonProcessing
                    }
                >
                    {isSimulationRunning
                        ? "Simulation Running"
                        : submitting
                          ? "Saving..."
                          : readOnly
                            ? "Read-only"
                            : "Save Changes"}
                </Button>
            </div>
            <div data-for="syncTip" data-tip={"Syncing the model will erase all changes!"}>
                {(isNewSnapshot || isSyncing) && simulationStatus !== "RUNNING" && (
                    <Button
                        className={isSyncing ? `${classes.syncDrawing}` : ""}
                        type="hollow"
                        style={{ width: "fit-content", minWidth: "unset", marginLeft: "0.5rem" }}
                        icon={ResetIcon}
                        smallPadding
                        onClick={async () => {
                            fetchModelData();
                        }}
                        // disabled={submitting || invalid || !isDataChanged || readOnly}
                    />
                )}
            </div>
            <SnackbarAlert
                open={isSimulationRunning}
                message={"Simulation in progress. Do not make changes to the model."}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
                direction="down"
                onExited={() => {}}
                top={"60px"}
            />

            <style>
                {`
              .MuiInputBase-root::before {
                border-bottom: none !important;
              }
              .MuiInput-underline.Mui-focused:after {
                border-bottom: none !important;
              }
              .Mui-selected {
                background-color: white !important;
                font-weight: bold
              }
              .MuiListItem-button:hover {
                background-color: #F0F8FD !important;
                color: #0049C6 !important;
              }
              .MuiList-root {
                padding-top: 0 !important;
                padding-bottom: 0 !important;
              }
              `}
            </style>
        </div>
    );
};

const mapStateToProps = createStructuredSelector({
    formErrors: selectFormErrors,
});

const mapDispatchToProps = (dispatch) => ({
    fetchDrawingData: (modelId) => dispatch(fetchDrawingData(modelId)),
    toggleContentOpen: () => dispatch(toggleContentOpen()),
    changeDataentryMode: (value) => dispatch(changeDataentryMode(value)),
    setSaveTimeout: (timeoutFunc) => dispatch(setSaveTimeout(timeoutFunc)),
    toggleImageGallery: () => dispatch(toggleImageGallery()),
    setPrimaryUnits: (primaryUnits) => dispatch(setPrimaryUnits(primaryUnits)),
});

export default connect(mapStateToProps, mapDispatchToProps)(TopButtons);
